import { useState } from 'react'
import Column from '@app/components/layout/Column'
import { Text } from '@app/components/core/typography/Text'
import Row from '@app/components/layout/Row'
import { Tag } from '@app/components/core/tag/Tag'
import { SelectableTagModel } from '@app/types/TagModel'
import { useTranslations } from '@i18n/useLanguage'
import { TextLink } from '@app/components/core/link/TextLink'
import { faMinus, faPlus } from '@fortawesome/pro-regular-svg-icons'

export type TagSelectionProps<TKey extends string | number> = {
  label: string
  subTitle: string
  tags: SelectableTagModel<TKey>[]
  collapsedAmountVisible?: number
  onSelect: (id: TKey) => void
}

export const TagSelection = <TKey extends string | number>({
  label,
  subTitle,
  tags,
  collapsedAmountVisible = 5,
  onSelect,
}: TagSelectionProps<TKey>) => {
  const translations = useTranslations().app.common
  const [expanded, setExpanded] = useState(false)
  const hasMore = tags.length > collapsedAmountVisible
  const shownTags = expanded ? tags : tags.slice(0, collapsedAmountVisible)

  return (
    <Column gap={'0.5rem'}>
      <Column gap={0}>
        <Text.Medium isBold={true}>{label}</Text.Medium>
        <Text.Small color={'var(--palette-blue-400)'}>{subTitle}</Text.Small>
      </Column>
      <Row
        style={{
          flexWrap: 'wrap',
          gap: '0.5rem',
        }}
      >
        {shownTags.map((tag) => (
          <Tag
            key={tag.key}
            label={tag.value}
            variant={tag.selected ? 'subtle' : 'transparent'}
            size="small"
            onClick={() => onSelect(tag.key)}
          />
        ))}
        {hasMore && (
          <TextLink
            icon={expanded ? faMinus : faPlus}
            iconPosition="left"
            onClick={() => setExpanded((prev) => !prev)}
          >
            {expanded ? translations.less : translations.more}
          </TextLink>
        )}
      </Row>
    </Column>
  )
}
